import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useAuthStore from '@store/useAuthStore';
import api from '@/api/api';

export const useSiteDetails = () => {
  const { project_code, company_id } = useParams();
  const user = useAuthStore((state) => state.user);

  const [isLoadedState, setLoadedState] = useState(false);
  const [EstimateProject, setEstimateProject] = useState(null);
  const [EstimateSites, setEstimateSites] = useState([]);
  const [ActiveEstimateSite, setActiveEstimateSite] = useState(null);
  const [EstimateCycle, setEstimateCycle] = useState([]);
  const [ProjectDocuments, setProjectDocuments] = useState([]);
  const [RequiredDocuments, setRequiredDocuments] = useState([]);
  const [OptionalDocuments, setOptionalDocuments] = useState([]);

  useEffect(() => {
    const ReadEstimateProjectData = async (project_code, company_id) => {
      try {
        setLoadedState(false);

        // 프로젝트 정보 조회 (여러 API 패턴 시도)
        let Project = null;
        let ProjectData = null;

        try {
          // 첫 번째 시도: /projects 엔드포인트
          const ResProject = await api.get('/projects', { params: { project_code } });
          Project = ResProject.data;
        } catch (error) {
          console.warn('⚠️ /projects API failed, trying alternative endpoint');
        }

        // 프로젝트 데이터 검증
        if (!Project || (Array.isArray(Project) && Project.length === 0)) {
          console.warn('⚠️ Project data is empty or null');
          throw new Error('Project data not found');
        }

        // 배열인 경우 첫 번째 항목 선택
        Project.estimates = Project.estimates.filter(
          ({ company_id }) => company_id === user.company_id
        );

        ProjectData = Project;

        // 사업장 조회
        const ResSites = await api.get('/sites', { params: { company_id } });
        const Sites = ResSites.data.map(({ id, site_name }) => ({ id, key: id, tab: site_name }));

        // 프로젝트별 증빙서류 조회
        const ResDocuments = await api.get('projects/documents', {
          params: { project_code: project_code },
        });
        const Documents = ResDocuments.data || [];

        // 필수/선택 증빙서류 분류 (API 응답 구조에 맞게 수정)
        const RequiredDocs = Documents.filter((doc) => doc.is_required === true).map((doc) => ({
          id: doc.id,
          document_type_id: doc.document_type_id,
          document_type: doc.document_type.type_code,
          key: doc.document_type.type_code,
          type_name_ko: doc.document_type.type_name_ko,
          type_name_en: doc.document_type.type_name_en,
          type_name_zh: doc.document_type.type_name_zh,
          is_required: doc.is_required,
          order_index: doc.order_index,
        }));

        const OptionalDocs = Documents.filter((doc) => doc.is_required === false).map((doc) => ({
          id: doc.id,
          document_type_id: doc.document_type_id,
          document_type: doc.document_type.type_code,
          key: doc.document_type.type_code,
          type_name_ko: doc.document_type.type_name_ko,
          type_name_en: doc.document_type.type_name_en,
          type_name_zh: doc.document_type.type_name_zh,
          is_required: doc.is_required,
          order_index: doc.order_index,
        }));

        if (Sites.length > 0) {
          setActiveEstimateSite(Sites[0]['key']);
        }

        setEstimateProject(ProjectData);
        setEstimateSites(Sites);
        setProjectDocuments(Documents);
        setRequiredDocuments(RequiredDocs);
        setOptionalDocuments(OptionalDocs);
      } catch (error) {
        console.error('Error fetching project data:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: error.config,
        });
        // 오류 발생 시 기본값 설정
        setEstimateProject(null);
        setEstimateSites([]);
        setProjectDocuments([]);
        setRequiredDocuments([]);
        setOptionalDocuments([]);
      } finally {
        setLoadedState(true);
      }
    };

    ReadEstimateProjectData(project_code, company_id);
  }, [project_code, company_id, user.company_id]);

  useEffect(() => {
    if (ActiveEstimateSite !== null) {
      const requestEstimateCycle = async (site_id) => {
        try {
          const ResCycles = await api.get(`/sites/${site_id}/cycles`);
          setEstimateCycle(ResCycles.data);
        } catch (error) {
          console.error('Error fetching cycles:', error);
        }
      };

      requestEstimateCycle(ActiveEstimateSite);
    }
  }, [ActiveEstimateSite]);

  // 사업장 리스트 다시 불러오는 함수
  const refreshSitesList = async () => {
    try {
      const ResSites = await api.get('/sites', { params: { company_id } });
      const Sites = ResSites.data.map(({ id, site_name }) => ({ id, key: id, tab: site_name }));
      setEstimateSites(Sites);
      return Sites;
    } catch (error) {
      console.error('Error refreshing sites list:', error);
      return [];
    }
  };

  // 사이클 데이터 새로고침 함수
  const refreshCycleData = async (site_id = ActiveEstimateSite) => {
    if (!site_id) return;

    try {
      const ResCycles = await api.get(`/sites/${site_id}/cycles`);
      setEstimateCycle(ResCycles.data);
      return ResCycles.data;
    } catch (error) {
      console.error('Error refreshing cycle data:', error);
      return [];
    }
  };

  return {
    project_code,
    company_id,
    user,
    isLoadedState,
    EstimateProject,
    EstimateSites,
    setEstimateSites,
    ActiveEstimateSite,
    setActiveEstimateSite,
    EstimateCycle,
    setEstimateCycle,
    ProjectDocuments,
    RequiredDocuments,
    OptionalDocuments,
    refreshSitesList,
    refreshCycleData,
  };
};

export const useModalStates = () => {
  const [isShowProjectAnnouncementModal, showProjectAnnouncementModal] = useState(false);
  const [isShowProjectAgreeModal, setShowProjectAgreeModal] = useState(false);
  const [isShowUsersModal, setShowUsersModal] = useState(false);
  const [isShowSiteEditModal, setSiteEditModal] = useState(false);
  const [isShowScope3TemplateModal, setShowScope3TemplateModal] = useState(false);
  const [isSiteEditModal, setSiteModalEdittype] = useState(false);

  return {
    isShowProjectAnnouncementModal,
    showProjectAnnouncementModal,
    isShowProjectAgreeModal,
    setShowProjectAgreeModal,
    isShowUsersModal,
    setShowUsersModal,
    isShowSiteEditModal,
    setSiteEditModal,
    isShowScope3TemplateModal,
    setShowScope3TemplateModal,
    isSiteEditModal,
    setSiteModalEdittype,
  };
};

export const useUserManagement = () => {
  const [siteUsers, setSiteUsers] = useState([]);
  const [editUser, setEditingUser] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isPasswordChange, setPasswordChange] = useState(false);

  const setEditUser = (_user) => {
    if (typeof editUser.id === 'undefined' || (editUser && editUser.id !== _user.id)) {
      setEditingUser(_user);
    } else if (editUser && editUser.id === _user.id) {
      setEditingUser({});
    }
    setIsDirty(false);
    setPasswordChange(false);
  };

  const addNewUser = () => {
    const _new_user = {
      id: null,
      company_name: '(HL만도 협력사)',
      username: null,
      email: null,
      phone: null,
      'current-password': null,
    };
    setEditingUser(_new_user);
    return _new_user;
  };

  const openUserModal = () => {
    setSiteUsers([
      {
        id: 1,
        company_name: '(협력사 기업명)',
        username: '참여자 1',
        email: 'user1@company.co.kr',
        phone: '010-1111-2222',
        'current-password': '!qesg2493',
        'new-password': null,
        'new-password-confirm': null,
      },
      {
        id: 2,
        company_name: '(협력사 기업명)',
        username: '참여자 2',
        email: 'user2@company.co.kr',
        phone: '010-1111-2222',
        'current-password': '!qesg2493',
        'new-password': null,
        'new-password-confirm': null,
      },
      {
        id: 3,
        company_name: '(협력사 기업명)',
        username: '참여자 3',
        email: 'user3@company.co.kr',
        phone: '010-1111-2222',
        'current-password': '!qesg2493',
        'new-password': null,
        'new-password-confirm': null,
      },
    ]);
  };

  const resetUserModal = () => {
    setPasswordChange(false);
    setIsDirty(false);
    setEditingUser({});
  };

  return {
    siteUsers,
    setSiteUsers,
    editUser,
    setEditUser,
    isDirty,
    setIsDirty,
    isPasswordChange,
    setPasswordChange,
    addNewUser,
    openUserModal,
    resetUserModal,
  };
};

export const useSiteManagement = () => {
  const user = useAuthStore((state) => state.user);

  const saveSiteEditedNewSite = async (siteData, isEdit, siteId, projectId) => {
    try {
      const { site_name, site_country, site_address, client_corporate } = siteData;
      let ResSite = null;

      if (!isEdit) {
        const ResNewSite = await api.post('/sites', {
          project_id: projectId,
          company_id: user.company_id,
          site_name: site_name,
          site_location: site_address,
          site_country: site_country,
          site_clients: client_corporate,
        });
        ResSite = ResNewSite.data;
      } else {
        const ResUpdatedSite = await api.put(`/sites/${siteId}`, {
          company_id: user.company_id,
          site_name: site_name,
          site_location: site_address,
          site_country: site_country,
          site_clients: client_corporate,
        });
        ResSite = ResUpdatedSite.data;
      }

      return ResSite;
    } catch (error) {
      console.error('Error saving site:', error);
      throw error;
    }
  };

  return {
    saveSiteEditedNewSite,
  };
};
